/**
 * Integrytas Group - API Rate Limiting (ARL)
 * Page: ARL.CONFIG
 *
 * Configure multi-dimensional rate-limit rules, endpoint token costs, and review
 * recent 429 violations. Buckets live in an UNLOGGED table; "Reset" clears them.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'ARL.CONFIG';

const DIMS = ['GLOBAL', 'IP', 'USER', 'WORKSPACE', 'ROLE'];
const MATCH_TYPES = ['RPC', 'RPC_PREFIX', 'TABLE', 'KIND'];
interface RuleRow { arl_rule_id: number; internal_code: string; name: string; dimension: string; scope_key: string | null; limit_tokens: number; window_seconds: number; is_active: 'Y' | 'N' }
interface CostRow { arl_endpoint_cost_id: number; match_type: string; match_value: string; cost: number; is_active: 'Y' | 'N' }
interface ViolRow { arl_violation_log_id: number; dimension: string | null; endpoint: string | null; cost: number | null; ip_address: string | null; user_id: number | null; created_at: string }

const VIOL_GRID_COLS: GridColumnDef[] = [
  { key: 'created_at',  header: 'When',      defaultWidth: 180 },
  { key: 'dimension',   header: 'Dimension', defaultWidth: 120 },
  { key: 'endpoint',    header: 'Endpoint',  defaultWidth: 200 },
  { key: 'cost',        header: 'Cost',      defaultWidth: 80  },
  { key: 'ip_address',  header: 'IP',        defaultWidth: 140 },
  { key: 'actor',       header: 'User',      defaultWidth: 160 },
];

export interface ArlConfigPageProps { session: AuthSession }

export function ArlConfigPage({ session }: ArlConfigPageProps) {
  return (
    <PersonalizationProvider pageCode="ARL.CONFIG" session={session}>
      <ArlConfigPageInner session={session} />
    </PersonalizationProvider>
  );
}

function ArlConfigPageInner({ session }: ArlConfigPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [costs, setCosts] = useState<CostRow[]>([]);
  const [viols, setViols] = useState<ViolRow[]>([]);
  const [userNames, setUserNames] = useState<Record<number, string>>({});
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // §17.B12: filter the is_active-bearing rules list — free-text + an Active facet
  // defaulting to Active-only on first load.
  const [ruleFilter, setRuleFilter] = useState('');
  const [facets, setFacets] = useState<{ active: string[] }>({ active: ['Y'] });

  const facetOptions = useMemo(() => ({
    active: [
      { value: 'Y', label: t('common.boolean.active', 'Active') },
      { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
    ],
  }), [t]);

  const filteredRules = useMemo(() => {
    const q = ruleFilter.trim().toLowerCase();
    return rules.filter(r =>
      (facets.active.length === 0 || facets.active.includes(r.is_active)) &&
      (q === '' || r.internal_code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q)),
    );
  }, [rules, ruleFilter, facets]);

  const anyRuleFilterActive = facets.active.length > 0 || ruleFilter.trim().length > 0;
  const clearRuleFilters = () => { setRuleFilter(''); setFacets({ active: [] }); };

  const detail = useExpandedRows<number>();

  // §17.B13 related children (expand/contract) — the user a 429 violation was
  // attributed to, resolved to a name via userNames (the raw id rides in `title`).
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => {
    const row = viols.find(v => v.arl_violation_log_id === id);
    if (!row || row.user_id == null) return [];
    return [
      { key: 'user', title: t('arl.related.user', 'User'), items: [{ id: row.user_id, label: userNames[row.user_id] ?? '—', title: String(row.user_id) }] },
    ];
  };
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [r, c, v, p] = await Promise.all([
        apiDb.from('arl_rule').select('arl_rule_id, internal_code, name, dimension, scope_key, limit_tokens, window_seconds, is_active').order('internal_code'),
        apiDb.from('arl_endpoint_cost').select('arl_endpoint_cost_id, match_type, match_value, cost, is_active').order('match_type'),
        apiDb.from('arl_violation_log').select('arl_violation_log_id, dimension, endpoint, cost, ip_address, user_id, created_at').order('created_at', { ascending: false }).limit(50),
        apiDb.from('csm_system_parameters').select('parameter_value').eq('internal_code', 'ARL.ENABLED').maybeSingle(),
      ]);
      if (r.error) throw r.error;
      const violRows = (v.data ?? []) as ViolRow[];
      setRules((r.data ?? []) as RuleRow[]); setCosts((c.data ?? []) as CostRow[]); setViols(violRows);
      setEnabled(((p.data as { parameter_value?: string } | null)?.parameter_value ?? 'Y') === 'Y');

      // Resolve violator user ids to a human label (display_name || email).
      const userIds = Array.from(new Set(violRows.map(vr => vr.user_id).filter((id): id is number => id != null)));
      if (userIds.length > 0) {
        const { data: uRows } = await apiDb
          .from('csm_user_master')
          .select('user_id, display_name, email')
          .in('user_id', userIds);
        const map: Record<number, string> = {};
        for (const u of (uRows ?? []) as { user_id: number; display_name: string | null; email: string | null }[]) {
          map[u.user_id] = u.display_name || u.email || '—';
        }
        setUserNames(map);
      } else {
        setUserNames({});
      }
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const toggleEnabled = async () => {
    const next = !enabled; setEnabled(next);
    try { await routedUpdate(session, 'csm_system_parameters', 'internal_code', 'ARL.ENABLED', { parameter_value: next ? 'Y' : 'N' }); }
    catch (e) { setError((e as Error).message); }
  };
  const resetBuckets = async () => {
    setBusy(true); setError(null); setNotice(null);
    try { const { data } = await apiDb.rpc('arl_reset', { p_rule_code: null }); setNotice(t('arl.notice.cleared', 'Cleared {{n}} bucket(s).', { n: (data as { cleared?: number })?.cleared ?? 0 })); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const addRule = async () => { await routedInsert(session, 'arl_rule', { internal_code: `ARL.RULE_${Date.now()}`, name: 'New rule', dimension: 'IP', limit_tokens: 600, window_seconds: 60, is_active: 'N' }); await reload(); };
  const saveRule = async (r: RuleRow) => { await routedUpdate(session, 'arl_rule', 'arl_rule_id', r.arl_rule_id, { name: r.name, dimension: r.dimension, scope_key: r.scope_key || null, limit_tokens: r.limit_tokens, window_seconds: r.window_seconds, is_active: r.is_active }); };
  const delRule = async (id: number) => { await routedDelete(session, 'arl_rule', 'arl_rule_id', id); setRules(rs => rs.filter(x => x.arl_rule_id !== id)); };

  const addCost = async () => { await routedInsert(session, 'arl_endpoint_cost', { match_type: 'KIND', match_value: '', cost: 1, is_active: 'Y' }); await reload(); };
  const saveCost = async (c: CostRow) => { await routedUpdate(session, 'arl_endpoint_cost', 'arl_endpoint_cost_id', c.arl_endpoint_cost_id, { match_type: c.match_type, match_value: c.match_value, cost: c.cost, is_active: c.is_active }); };
  const delCost = async (id: number) => { await routedDelete(session, 'arl_endpoint_cost', 'arl_endpoint_cost_id', id); setCosts(cs => cs.filter(x => x.arl_endpoint_cost_id !== id)); };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('arl.title', 'Rate Limiting')}</span>
        <label className="flex items-center gap-1 text-csm-xs text-csm-ink-soft"><input type="checkbox" checked={enabled} onChange={() => void toggleEnabled()} /> {t('arl.enabled', 'Enabled')}</label>
        {canChange && (<button onClick={() => void resetBuckets()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('arl.resetBuckets', 'Reset Buckets')}</button>)}
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('arl.refresh', 'Refresh')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('arl.working', 'Working…')}</span>}
        {notice && <span className="text-csm-xs text-primary">{notice}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      <div className="flex-1 overflow-auto p-3">
        {error && (
          <ErrorBanner
            message={t('arl.load_failed', "Couldn't load rate-limiting configuration.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <Section title={t('arl.section.rules', 'Rate Limit Rules')} onAdd={addRule}>
          <div className="mb-2 flex flex-wrap items-center gap-2 border-b border-csm-border pb-2">
            <input
              type="text"
              value={ruleFilter}
              onChange={e => setRuleFilter(e.target.value)}
              placeholder={t('arl.rules.search', 'Search rules…')}
              className="h-7 w-56 rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none"
            />
            <FacetFilter label={t('common.boolean.active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <ClearFiltersButton active={anyRuleFilterActive} onClear={clearRuleFilters} label={t('common.clear_filters', 'Clear filters')} />
          </div>
          {filteredRules.map((r) => {
            const i = rules.findIndex(x => x.arl_rule_id === r.arl_rule_id);
            return (
            <div key={r.arl_rule_id} className="mb-1 flex flex-wrap items-center gap-1">
              <span className="w-40 font-mono text-csm-2xs text-csm-ink-soft">{r.internal_code}</span>
              <input value={r.name} onChange={e => setRules(rs => rs.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} onBlur={() => void saveRule(rules[i])} className="h-7 w-36 rounded border border-csm-border px-1 text-csm-xs" />
              <select value={r.dimension} onChange={e => setRules(rs => rs.map((x, j) => j === i ? { ...x, dimension: e.target.value } : x))} onBlur={() => void saveRule(rules[i])} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{DIMS.map(d => <option key={d} value={d}>{d}</option>)}</select>
              <input value={r.scope_key ?? ''} onChange={e => setRules(rs => rs.map((x, j) => j === i ? { ...x, scope_key: e.target.value } : x))} onBlur={() => void saveRule(rules[i])} placeholder={t('arl.placeholder.scope', 'scope (blank=all)')} className="h-7 w-28 rounded border border-csm-border px-1 font-mono text-csm-2xs" />
              <input type="number" value={r.limit_tokens} onChange={e => setRules(rs => rs.map((x, j) => j === i ? { ...x, limit_tokens: Number(e.target.value) } : x))} onBlur={() => void saveRule(rules[i])} className="h-7 w-16 rounded border border-csm-border px-1 text-csm-2xs" title={t('arl.tip.tokens', 'tokens')} />
              <span className="text-csm-2xs text-csm-watermark">/</span>
              <input type="number" value={r.window_seconds} onChange={e => setRules(rs => rs.map((x, j) => j === i ? { ...x, window_seconds: Number(e.target.value) } : x))} onBlur={() => void saveRule(rules[i])} className="h-7 w-14 rounded border border-csm-border px-1 text-csm-2xs" title={t('arl.tip.seconds', 'seconds')} />
              <label className="flex items-center gap-1 text-csm-2xs"><input type="checkbox" checked={r.is_active === 'Y'} onChange={e => { setRules(rs => rs.map((x, j) => j === i ? { ...x, is_active: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveRule(rules[i])} /> {t('arl.on', 'on')}</label>
              {canDelete && (<button onClick={() => void delRule(r.arl_rule_id)} className="text-csm-danger text-csm-2xs">{t('arl.del', 'del')}</button>)}
            </div>
            );
          })}
          {rules.length > 0 && filteredRules.length === 0 && (
            <EmptyState variant="no-match" title={t('arl.empty.rules_filtered', 'No rules match the filter.')} />
          )}
        </Section>

        <Section title={t('arl.section.costs', 'Endpoint Costs (default 1)')} onAdd={addCost}>
          {costs.map((c, i) => (
            <div key={c.arl_endpoint_cost_id} className="mb-1 flex flex-wrap items-center gap-1">
              <select value={c.match_type} onChange={e => setCosts(cs => cs.map((x, j) => j === i ? { ...x, match_type: e.target.value } : x))} onBlur={() => void saveCost(costs[i])} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{MATCH_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select>
              <input value={c.match_value} onChange={e => setCosts(cs => cs.map((x, j) => j === i ? { ...x, match_value: e.target.value } : x))} onBlur={() => void saveCost(costs[i])} placeholder={t('arl.placeholder.match', 'match (rpc / table / kind)')} className="h-7 w-44 rounded border border-csm-border px-1 font-mono text-csm-2xs" />
              <span className="text-csm-2xs text-csm-watermark">→</span>
              <input type="number" value={c.cost} onChange={e => setCosts(cs => cs.map((x, j) => j === i ? { ...x, cost: Number(e.target.value) } : x))} onBlur={() => void saveCost(costs[i])} className="h-7 w-16 rounded border border-csm-border px-1 text-csm-2xs" title={t('arl.tip.cost', 'cost')} />
              <label className="flex items-center gap-1 text-csm-2xs"><input type="checkbox" checked={c.is_active === 'Y'} onChange={e => { setCosts(cs => cs.map((x, j) => j === i ? { ...x, is_active: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveCost(costs[i])} /> {t('arl.on', 'on')}</label>
              {canDelete && (<button onClick={() => void delCost(c.arl_endpoint_cost_id)} className="text-csm-danger text-csm-2xs">{t('arl.del', 'del')}</button>)}
            </div>
          ))}
        </Section>

        <div className="rounded border border-csm-border bg-white p-3">
          <div className="mb-2 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('arl.section.violations', 'Recent Violations (429)')}</div>
          <PersonalizedGrid componentKey="grid" columns={VIOL_GRID_COLS}>
            {(visibleCols, widths) => (
              <table className="min-w-full w-max border-collapse text-csm-2xs">
                <thead className="text-csm-2xs uppercase text-csm-ink-soft">
                  <tr>
                    <th className="w-7 px-2 text-left" title={t('arl.related.header', 'Related records')} />
                    {visibleCols.map(col => (
                      <th key={col.key} className={`px-2 text-left${col.key === 'cost' ? ' csm-num' : ''}`}
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t(`arl.col.${col.key}`, col.header)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {viols.map(v => (
                    <Fragment key={v.arl_violation_log_id}>
                    <tr className="hover:bg-csm-surface-alt">
                      <td className="px-2 py-0.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                        <RelatedExpandButton open={detail.isOpen(v.arl_violation_log_id)} hasChildren={hasRelated(v.arl_violation_log_id)} onToggle={() => detail.toggle(v.arl_violation_log_id)} expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className={`px-2 py-0.5${col.key === 'cost' ? ' csm-num' : ''}`}
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'created_at' && <span className="text-csm-watermark">{new Date(v.created_at).toLocaleString()}</span>}
                          {col.key === 'dimension'  && v.dimension}
                          {col.key === 'endpoint'   && <span className="font-mono">{v.endpoint}</span>}
                          {col.key === 'cost'       && v.cost}
                          {col.key === 'ip_address' && <span className="font-mono">{v.ip_address ?? '—'}</span>}
                          {col.key === 'actor'      && (v.user_id == null ? '—' : (userNames[v.user_id] ?? '—'))}
                        </td>
                      ))}
                    </tr>
                    {detail.isOpen(v.arl_violation_log_id) && (
                      <tr><td colSpan={visibleCols.length + 1} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRelatedGroups(v.arl_violation_log_id)} session={session} /></td></tr>
                    )}
                    </Fragment>
                  ))}
                  {viols.length === 0 && <tr><td colSpan={visibleCols.length + 1} className="px-2 py-3"><EmptyState variant="all-clear" title={t('arl.empty.violations', 'No violations.')} /></td></tr>}
                </tbody>
              </table>
            )}
          </PersonalizedGrid>
        </div>
      </div>
    </div>
  );
}

function Section({ title, onAdd, children }: { title: string; onAdd: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="mb-4 rounded border border-csm-border bg-white p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{title}</span>
        <button onClick={onAdd} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('arl.add', '+ Add')}</button>
      </div>
      {children}
    </div>
  );
}
